import { gql } from '@apollo/client';
import { FunctionWithFragments } from '@modernloop/shared/components';

import {
  GetBasicAvailabilityOptionsConfigV2_SettingsFragment,
  JobStageInterviewGroupInput,
} from 'src/generated/mloop-graphql';

import {
  DEFAULT_MINIMUM_TIME_BLOCK_MINUTES,
  DEFAULT_MINUTES_PER_DAY,
  DEFAULT_NUMBER_OF_DAYS,
} from 'src/store/slices/candidate-availability-options';

import { isDayBreak } from 'src/utils/interview';

import { DAY_CALC_FROM_INTERVIEW_PLAN, DURATION_CALC_FROM_INTERVIEW_PLAN } from 'src/constants';

type BasicAvailabilityOptionsConfigFragments = {
  settings?: GetBasicAvailabilityOptionsConfigV2_SettingsFragment;
};

export const getBasicAvailabilityOptionsConfigV2: FunctionWithFragments<
  BasicAvailabilityOptionsConfigFragments,
  { groups: JobStageInterviewGroupInput[] },
  {
    numberOfDays: number;
    minutesPerDays: number;
    minimumTimeBlockMinutes: number;
  }
> = ({ settings }, { groups }) => {
  const savedOptions = settings;
  let numOfDays = DEFAULT_NUMBER_OF_DAYS;
  let minsPerDays = 0;
  let currentDay = 1;
  const minsPerDaysMap = new Map<number, number>();
  let minTimeBlockMinutes = 0;
  const interviews = groups?.map((group) => group?.jobStageInterviews).flat();
  if (interviews && interviews.length) {
    interviews.forEach((interview, index) => {
      if (interview?.interviewType && isDayBreak(interview?.interviewType)) {
        // Minimum number of days is 1, each day break adds an additional day
        if (numOfDays === DEFAULT_NUMBER_OF_DAYS) {
          numOfDays = 1;
        }
        numOfDays += 1;
      }

      if (interview?.duration && minTimeBlockMinutes < interview?.duration) {
        minTimeBlockMinutes = interview.duration;
      }

      // If the previous interview is a day break, we need to save the mins per day and reset
      const previousInterviewType = index !== 0 ? interviews[index - 1]?.interviewType : undefined;
      if (previousInterviewType && isDayBreak(previousInterviewType)) {
        minsPerDaysMap.set(currentDay, minsPerDays);
        minsPerDays = 0;
        currentDay += 1;
      }

      minsPerDays += interview?.duration || 0;
    });
    minsPerDaysMap.set(currentDay, minsPerDays);
  } else {
    minsPerDays = DEFAULT_MINUTES_PER_DAY;
    minTimeBlockMinutes = DEFAULT_MINIMUM_TIME_BLOCK_MINUTES;
  }

  // If multiple days, get max mins per day. Since availability doesn't support different mins per day, we use the max
  minsPerDays = minsPerDaysMap.size > 0 ? Math.max(...Array.from(minsPerDaysMap.values())) : minsPerDays;

  const exactMinsPerDay =
    savedOptions?.minutesPerDays?.value !== DURATION_CALC_FROM_INTERVIEW_PLAN
      ? (savedOptions?.minutesPerDays?.value ?? minsPerDays)
      : minsPerDays;

  const exactMinimumTimeBlockMinutes =
    savedOptions?.minimumTimeBlockMinutes?.value !== DURATION_CALC_FROM_INTERVIEW_PLAN
      ? (savedOptions?.minimumTimeBlockMinutes?.value ?? minTimeBlockMinutes)
      : minTimeBlockMinutes;

  return {
    numberOfDays:
      savedOptions?.numberOfDays?.value !== DAY_CALC_FROM_INTERVIEW_PLAN
        ? (savedOptions?.numberOfDays?.value ?? numOfDays)
        : numOfDays,
    minutesPerDays: exactMinsPerDay,
    minimumTimeBlockMinutes: exactMinimumTimeBlockMinutes,
  };
};

getBasicAvailabilityOptionsConfigV2.fragments = {
  settings: gql`
    fragment getBasicAvailabilityOptionsConfigV2_settings on JobStageCandidateAvailabilitySettingsV2 {
      numberOfDays {
        value
      }
      minutesPerDays {
        value
      }
      minimumTimeBlockMinutes {
        value
      }
    }
  `,
};
